import type { WalletPendingBreakdown, WalletPendingCategory } from '../dtos/wallet';

export const EMPTY_WALLET_PENDING_BREAKDOWN: WalletPendingBreakdown = {
  awaitingWhopSetup: 0,
  awaitingBrandPayment: 0,
  readyToTransfer: 0,
  transferError: 0,
};

export interface ClassifyCollabPendingCategoryInput {
  hasWhopAccount: boolean;
  whopPaymentId?: string;
  /** Whop payment status string, or `null` when the lookup failed. */
  whopPaymentStatus?: string | null;
}

function isBrandPaymentSucceeded(status: string): boolean {
  return status === 'succeeded' || status === 'paid';
}

export function classifyCollabPendingCategory(
  input: ClassifyCollabPendingCategoryInput,
): WalletPendingCategory {
  if (!input.hasWhopAccount) {
    return 'awaitingWhopSetup';
  }

  if (!input.whopPaymentId) {
    return 'readyToTransfer';
  }

  if (input.whopPaymentStatus === null || input.whopPaymentStatus === undefined) {
    return 'transferError';
  }

  if (!isBrandPaymentSucceeded(input.whopPaymentStatus)) {
    return 'awaitingBrandPayment';
  }

  return 'readyToTransfer';
}

export function classifyLcOrderPendingCategory(hasWhopAccount: boolean): WalletPendingCategory {
  return hasWhopAccount ? 'readyToTransfer' : 'awaitingWhopSetup';
}

export function addToPendingBreakdown(
  breakdown: WalletPendingBreakdown,
  category: WalletPendingCategory,
  amount: number,
): WalletPendingBreakdown {
  if (amount <= 0) {
    return breakdown;
  }
  return {
    ...breakdown,
    [category]: breakdown[category] + amount,
  };
}

export function sumPendingBreakdown(breakdown: WalletPendingBreakdown): number {
  return (
    breakdown.awaitingWhopSetup +
    breakdown.awaitingBrandPayment +
    breakdown.readyToTransfer +
    breakdown.transferError
  );
}

export function getPendingCategoryLabel(category: WalletPendingCategory): string {
  switch (category) {
    case 'awaitingWhopSetup':
      return 'Set up payouts';
    case 'awaitingBrandPayment':
      return 'Pending brand payment';
    case 'readyToTransfer':
      return 'Processing';
    case 'transferError':
      return 'Transfer issue';
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
}

export function getPendingCategoryTooltip(
  category: WalletPendingCategory,
  formattedAmount: string,
): string {
  switch (category) {
    case 'awaitingWhopSetup':
      return `${formattedAmount} is waiting — connect your payout account to receive it.`;
    case 'awaitingBrandPayment':
      return `${formattedAmount} is pending — it will be available once the brand's payment processes.`;
    case 'readyToTransfer':
      return `${formattedAmount} is processing — it will move to your withdrawable balance shortly.`;
    case 'transferError':
      return `${formattedAmount} could not be verified — refresh or contact support if this continues.`;
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
}
